import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatorPublishUtility = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category_tags: '',
    pricing_model: 'FREE',
    price_amount: '',
    currency: 'USD',
    duration_days: '',
    max_redemptions: '',
    fulfillment_type: 'DIGITAL',
    fulfillment_owner: 'PROVIDER',
    requirements: '',
    redeem_instructions: ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      alert('Title and description are required.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category_tags: form.category_tags ? form.category_tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        pricing_model: form.pricing_model,
        price_amount: form.price_amount ? parseFloat(form.price_amount) : null,
        currency: form.currency,
        duration_days: form.duration_days ? parseInt(form.duration_days) : null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        fulfillment_type: form.fulfillment_type,
        fulfillment_owner: form.fulfillment_owner,
        requirements: form.requirements || null,
        redeem_instructions: form.redeem_instructions || null
      };
      await axios.post(`${API}/provider/utility-listings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/creator/my-listings');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${theme.colors.borders}`,
    borderRadius: theme.radius.sm,
    fontSize: '14px',
    background: theme.colors.bg.card,
    color: theme.colors.text.primary,
    outline: 'none'
  };
  const labelStyle = { fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary, display: 'block', marginBottom: '6px' };

  return (
    <div style={{ padding: '40px', maxWidth: '700px' }}>
      <button
        data-testid="back-to-listings"
        onClick={() => navigate(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 0',
          background: 'none',
          border: 'none',
          color: theme.colors.text.secondary,
          fontSize: '14px',
          cursor: 'pointer',
          marginBottom: '24px'
        }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          background: `${theme.colors.accent.teal}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Package size={22} style={{ color: theme.colors.accent.teal }} />
        </div>
        <div>
          <h1 data-testid="publish-title" style={{
            fontFamily: theme.fonts.heading,
            fontSize: '24px',
            fontWeight: 700,
            color: theme.colors.text.primary
          }}>
            Publish Utility
          </h1>
          <p style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
            Create a new utility listing for the marketplace
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{
          background: theme.colors.bg.card,
          border: `1px solid ${theme.colors.borders}`,
          borderRadius: theme.radius.card,
          padding: '28px',
          boxShadow: theme.colors.shadows.sm,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* Title */}
          <div>
            <label style={labelStyle}>Title *</label>
            <input
              data-testid="listing-title"
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="e.g. 10% Store Discount"
              style={fieldStyle}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description *</label>
            <textarea
              data-testid="listing-description"
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="What does this utility offer?"
              rows={4}
              style={{ ...fieldStyle, resize: 'vertical' }}
              required
            />
          </div>

          {/* Category Tags */}
          <div>
            <label style={labelStyle}>Category Tags</label>
            <input
              data-testid="listing-tags"
              type="text"
              value={form.category_tags}
              onChange={(e) => update('category_tags', e.target.value)}
              placeholder="Comma-separated, e.g. Fashion, Discount, VIP"
              style={fieldStyle}
            />
          </div>

          {/* Pricing row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Pricing Model</label>
              <select data-testid="listing-pricing" value={form.pricing_model} onChange={(e) => update('pricing_model', e.target.value)} style={fieldStyle}>
                <option value="FREE">Free</option>
                <option value="PAID">Paid</option>
                <option value="REV_SHARE">Revenue Share</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Price Amount</label>
              <input
                data-testid="listing-price"
                type="number"
                value={form.price_amount}
                onChange={(e) => update('price_amount', e.target.value)}
                placeholder="0.00"
                style={fieldStyle}
                disabled={form.pricing_model === 'FREE'}
              />
            </div>
            <div>
              <label style={labelStyle}>Currency</label>
              <select data-testid="listing-currency" value={form.currency} onChange={(e) => update('currency', e.target.value)} style={fieldStyle}>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="ETH">ETH</option>
              </select>
            </div>
          </div>

          {/* Fulfillment row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Fulfillment Type</label>
              <select data-testid="listing-fulfillment" value={form.fulfillment_type} onChange={(e) => update('fulfillment_type', e.target.value)} style={fieldStyle}>
                <option value="DIGITAL">Digital</option>
                <option value="PHYSICAL">Physical</option>
                <option value="SERVICE">Service</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Fulfillment Owner</label>
              <select value={form.fulfillment_owner} onChange={(e) => update('fulfillment_owner', e.target.value)} style={fieldStyle}>
                <option value="PROVIDER">Provider</option>
                <option value="CREATOR">Creator</option>
              </select>
            </div>
          </div>

          {/* Limits row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Duration (days)</label>
              <input
                data-testid="listing-duration"
                type="number"
                value={form.duration_days}
                onChange={(e) => update('duration_days', e.target.value)}
                placeholder="Leave blank for unlimited"
                style={fieldStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Max Redemptions</label>
              <input
                data-testid="listing-max-redemptions"
                type="number"
                value={form.max_redemptions}
                onChange={(e) => update('max_redemptions', e.target.value)}
                placeholder="Leave blank for unlimited"
                style={fieldStyle}
              />
            </div>
          </div>

          {/* Redeem instructions */}
          <div>
            <label style={labelStyle}>Redeem Instructions</label>
            <textarea
              data-testid="listing-redeem-instructions"
              value={form.redeem_instructions}
              onChange={(e) => update('redeem_instructions', e.target.value)}
              placeholder="How do buyers redeem this utility?"
              rows={3}
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
          </div>

          {/* Requirements */}
          <div>
            <label style={labelStyle}>Requirements</label>
            <input
              data-testid="listing-requirements"
              type="text"
              value={form.requirements}
              onChange={(e) => update('requirements', e.target.value)}
              placeholder="Any requirements for redemption"
              style={fieldStyle}
            />
          </div>

          {/* Submit */}
          <button
            data-testid="publish-submit-btn"
            type="submit"
            disabled={loading}
            style={{
              padding: '14px',
              background: theme.colors.primary,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginTop: '4px'
            }}
          >
            {loading ? 'Publishing...' : 'Publish Listing'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatorPublishUtility;
